import WorkItemFormModel from "./WorkItemFormModel";
import WorkItemTransitionModel from "./WorkItemTransitionModel";

const getTransitions = (workItem) =>
  workItem.transitions
    .sort((a, b) => {
      if (a.processOrder > b.processOrder) return 1;
      if (a.processOrder < b.processOrder) return -1;
      return 0;
    })
    .map((transition) => new WorkItemTransitionModel(transition));

const getForms = (workItem) =>
  workItem.forms
    .filter((form) => form.createdOn && form.cleanData)
    .map((form) => new WorkItemFormModel(form))
    .sort((a, b) => {
      if (a.createdOn > b.createdOn) {
        return 1;
      }
      if (b.createdOn > a.createdOn) {
        return -1;
      }
      return 0;
    });

const extractCancellationReason = (note) => {
  if (!note) {
    return null;
  }
  const reasonIndex = note.indexOf("Reason:");
  if (reasonIndex !== -1) {
    return note.slice(reasonIndex + "Reason:".length).trim();
  }
  const cancelledByIndex = note.indexOf(". Cancelled by");
  if (cancelledByIndex !== -1) {
    return note.slice(0, cancelledByIndex).trim();
  }
  const vistaLinkMatch = note.match(
    /Cancelled(?:\sby\s.+?)?\svia VistaLink\.\s(.+?)\.*$/
  );
  if (vistaLinkMatch) {
    return vistaLinkMatch[1].replace(/\.$/, "").trim();
  }
  return note;
};

const getCancellationReason = (transitions, status) => {
  if (status !== "Cancelled" || transitions.length === 0) {
    return null;
  }
  const cancellationTransition = transitions
    .slice()
    .reverse()
    .find(
      (transition) =>
        transition.toStageType === "Cancelled" ||
        (transition.note || "").includes("Cancelled")
    );

  if (!cancellationTransition) {
    return null;
  }

  return extractCancellationReason(cancellationTransition.note);
};

const extractCancelledBy = (note) => {
  if (!note) return null;
  const oldFormat = note.match(/Cancelled via VistaLink by (.+?)\. Reason:/);
  if (oldFormat) return oldFormat[1];
  const newFormat = note.match(/Cancelled by (.+?) via VistaLink/);
  if (newFormat) return `${newFormat[1]} via VistaLink`;
  return null;
};

const getCanceledBy = (transitions, status) => {
  if (status !== "Cancelled" || transitions.length === 0) {
    return null;
  }

  const cancellationTransition = transitions
    .slice()
    .reverse()
    .find(
      (transition) =>
        transition.toStageType === "Cancelled" ||
        (transition.note || "").includes("Cancelled")
    );
  if (!cancellationTransition) {
    return null;
  }
  return (
    extractCancelledBy(cancellationTransition.note) ||
    cancellationTransition.transitionedBy ||
    null
  );
};

class WorkItemModel {
  constructor(workItem, workOrder) {
    this.id = workItem.workItemId || null;
    this.workitemtemplateid = workItem.workItemTemplateId || null;
    this.workitemtemplatedisplayname =
      workItem.workItemTemplateDisplayName || null;
    this.name = workItem.workItemTemplateDisplayName || null;
    this.reference = workItem.reference || null;
    this.createdOn = workItem.createdOn;
    this.targetDate = workOrder["start-by"] || null;
    this.scheduledDate = workItem.schedule?.startTime || null;
    this.type = null;
    this.category =
      workItem.fieldValues?.workordercategory?.title ||
      workItem.fieldValues?.workordercategory?.Title ||
      null;
    this.subCategory =
      workItem.fieldValues?.workordersubcategory?.title ||
      workItem.fieldValues?.workordersubcategory?.Title ||
      null;
    this.wiCategory =
      workItem.fieldValues?.category?.title ||
      workItem.fieldValues?.category?.Title ||
      null;
    this.assignedTo = workItem.assignedUserName || null;
    this.status = workItem.currentWorkflowStageDisplayName || null;
    this.history = workItem.fieldValues?.previoushistoryforproperty || null;
    this.notes = workItem.fieldValues?.description || null;
    this.currentWorkflowStageType = workItem.currentWorkflowStageType || null;
    this.completed = workItem.isCompleted;
    this.preferredAppointmentTimeslot =
      workItem.fieldValues?.preferredappointmenttimeslot?.title ||
      workItem.fieldValues?.preferredappointmenttimeslot?.Title ||
      null;
    this.preferredAppointmentDate =
      workItem.fieldValues?.preferredappointmentdate || null;
    this.workItemAgreement =
      workItem.fieldValues?.workitemagreement?.title ||
      workItem.fieldValues?.workitemagreement?.Title ||
      null;
    this.progressNotes = workItem.fieldValues?.progressnotes || "";
    this.progressStatus = workItem.fieldValues?.progressstatus || "";
    this.transitions = getTransitions(workItem) || [];
    this.cancellationReason = getCancellationReason(
      this.transitions,
      this.status
    );
    this.cancelledBy = getCanceledBy(this.transitions, this.status);
    this.forms = getForms(workItem) || [];
    this.closedDate = this.getClosedDate();
    this.workItemNotes = workItem.fieldValues?.workitemnotes || null;
    this.jobSpecificAccessNotes =
      workItem.fieldValues?.jobspecificaccessnotes || null;
    this.noAccessAttempts = workOrder.noaccessattempts || 0;
  }

  getClosedDate() {
    if (!this.completed || this.transitions.length === 0) {
      return null;
    }
    return this.transitions[this.transitions.length - 1].transitionedAt;
  }
}

export default WorkItemModel;
